import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, Send } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BluetoothTempButton from "./BluetoothTempButton";

function YesNoPill({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5" data-testid={testId}>
      {[
        { v: true,  label: "Yes", cls: "bg-[#E63946] text-white border-[#E63946]" },
        { v: false, label: "No",  cls: "bg-kawaii-mint/60 text-kawaii-text border-kawaii-mint" },
      ].map((o) => {
        const active = value === o.v;
        return (
          <button
            type="button" key={String(o.v)}
            onClick={() => onChange(active ? null : o.v)}
            data-testid={`${testId}-${o.v ? 'yes' : 'no'}`}
            className={`kawaii-btn px-3.5 py-1 rounded-full text-xs font-body font-semibold border ${
              active ? o.cls : 'bg-white text-kawaii-textSoft border-kawaii-border'
            }`}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

function Row({ label, value, onChange, testId, danger }) {
  return (
    <div className={`flex items-center justify-between gap-3 p-2.5 rounded-2xl border ${
      value === true && danger ? 'border-[#E63946]/60 bg-[#FFE5E5]/40' : 'border-kawaii-border bg-white'
    }`}>
      <span className="text-sm font-body text-kawaii-text pr-2">{label}</span>
      <YesNoPill value={value} onChange={onChange} testId={testId} />
    </div>
  );
}

const today = () => format(new Date(), "yyyy-MM-dd");

export default function CheckinForm({ episode, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [hasTemp, setHasTemp] = useState(episode.has_temperature ?? null);
  const [tempC, setTempC] = useState(episode.temperature_c ? String(episode.temperature_c) : "");
  const [achy, setAchy] = useState(episode.feels_achy ?? null);
  const [feverish, setFeverish] = useState(episode.feels_feverish ?? null);
  const [breaks, setBreaks] = useState(episode.skin_breaks ?? null);
  const [warm, setWarm] = useState(episode.warm_to_touch ?? null);
  const [streaks, setStreaks] = useState(episode.red_streaks ?? null);
  const [notes, setNotes] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/cellulitis/${episode.id}/checkins`, {
        date: today(),
        has_temperature: hasTemp,
        temperature_c: tempC ? parseFloat(tempC) : null,
        feels_achy: achy,
        feels_feverish: feverish,
        skin_breaks: breaks,
        warm_to_touch: warm,
        red_streaks: streaks,
        notes,
      });
      toast.success("Check-in logged ♡");

      // If concerning symptoms flagged, warm-start the emergency assessment
      const urgentFlagged = hasTemp === true || feverish === true || streaks === true;
      if (urgentFlagged) {
        window.dispatchEvent(new CustomEvent("open-emergency", { detail: { answers: {
          red_skin: null,
          increased_swelling: null,
          hot_to_touch: warm,
          aching: achy,
          fever_or_temperature: hasTemp,
          temperature_c: tempC || "",
          nauseous_or_faint: null,
          shaking_or_convulsing: null,
        }}}));
        toast.info("Opening urgent-care script — pre-filled from your check-in", { duration: 4000 });
      }
      onSaved && onSaved(data);
    } catch (err) {
      toast.error("Couldn't save check-in");
    } finally { setSaving(false); }
  };

  const urgent = hasTemp === true || feverish === true || streaks === true;

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      onSubmit={submit}
      className="mt-4 rounded-2xl bg-kawaii-bg/60 border border-kawaii-border p-4"
      data-testid={`checkin-form-${episode.id}`}
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-3">
        Today's check-in · {format(new Date(), "MMM d")}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <Row label="Do you have a temperature?" value={hasTemp} onChange={setHasTemp} testId={`ci-temp-${episode.id}`} danger />
        <div className={`flex items-center justify-between gap-3 p-2.5 rounded-2xl border ${hasTemp === true ? 'border-[#E63946]/60 bg-[#FFE5E5]/40' : 'border-kawaii-border bg-white'}`}>
          <span className="text-sm font-body text-kawaii-text pr-2">Reading (°C)</span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <input
              type="number" step="0.1" min="34" max="43"
              value={tempC} onChange={(e) => setTempC(e.target.value)}
              placeholder="37.5"
              data-testid={`ci-tempval-${episode.id}`}
              className="w-20 px-2 py-1 rounded-xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text text-center focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
            />
            <BluetoothTempButton onRead={(c) => { setTempC(c); setHasTemp(true); }} testId={`ci-bt-thermo-${episode.id}`} />
            <button
              type="button"
              onClick={() => setTempC("")}
              data-testid={`ci-tempval-unknown-${episode.id}`}
              className={`kawaii-btn text-[11px] px-2.5 py-1 rounded-full font-body font-semibold border ${
                tempC === "" && hasTemp === true ? 'bg-kawaii-lavender/60 border-kawaii-lavender text-kawaii-text' : 'bg-white border-kawaii-border text-kawaii-textSoft'
              }`}
            >Don't know</button>
          </div>
        </div>
        <Row label="Feeling achy?"       value={achy}     onChange={setAchy}     testId={`ci-achy-${episode.id}`} />
        <Row label="Feeling feverish?"   value={feverish} onChange={setFeverish} testId={`ci-feverish-${episode.id}`} danger />
        <Row label="Skin breaks?"        value={breaks}   onChange={setBreaks}   testId={`ci-breaks-${episode.id}`} />
        <Row label="Warm to the touch?"  value={warm}     onChange={setWarm}     testId={`ci-warm-${episode.id}`} />
        <Row label="Red streaks?"        value={streaks}  onChange={setStreaks}  testId={`ci-streaks-${episode.id}`} danger />
        <input
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything else? (optional)"
          data-testid={`ci-notes-${episode.id}`}
          className="px-3 py-2.5 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
        />
      </div>

      {urgent && (
        <div className="mt-3 rounded-2xl bg-[#FFE5E5]/60 border border-[#E63946]/50 p-3 text-xs text-kawaii-textSoft font-body leading-relaxed flex items-start gap-2" data-testid={`ci-urgent-${episode.id}`}>
          <span className="text-base leading-none">⚠️</span>
          <div className="flex-1">
            <div className="text-[#E63946] font-heading font-semibold">Please seek care today</div>
            <button
              type="button"
              onClick={() => {
                const parts = [];
                if (hasTemp === true) parts.push(`a temperature${tempC ? ` of ${tempC}°C` : ''}`);
                if (feverish === true) parts.push("feeling feverish");
                if (streaks === true) parts.push("red streaks spreading");
                const seed = `Your check-in today shows ${parts.join(' and ')} with active cellulitis. This is urgent — please call your GP or urgent care right now. I'm here while you sort it out ♡`;
                window.dispatchEvent(new CustomEvent("open-lymphie", { detail: { seedMessage: seed } }));
              }}
              data-testid={`ci-talk-lymphie-${episode.id}`}
              className="kawaii-btn mt-2 px-3 py-1 rounded-full bg-[#E63946] text-white text-[11px] font-semibold"
            >
              💬 Talk to Lymphie now
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end mt-3">
        <button
          type="submit" disabled={saving}
          data-testid={`ci-save-${episode.id}`}
          className="kawaii-btn px-4 py-2 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-body font-semibold text-xs shadow-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={2.5} />}
          Save check-in
        </button>
      </div>
    </motion.form>
  );
}
